import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useUserAccess } from '../../../context/UserAccessContext';
import { useAuth0Token } from '../../../hooks/useAuth0Token';
import { updateMyName } from '../../../services/userService';

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super Admin',
  'dashboard-admin': 'Admin',
  'dashboard-advisor': 'Advisor',
  planner: 'Planner'
};

/**
 * Self-service editor for the LOGIN ACCOUNT (users collection) — the identity
 * shown in the header greeting and account menu. Independent of any advisor
 * configuration: only the display name is editable here; role and access are
 * admin-managed in User Management.
 */
const MyAccountEditor: React.FC = () => {
  const { user: dbUser, role, refreshUser } = useUserAccess();
  const { token } = useAuth0Token();

  const [name, setName] = useState(dbUser?.name || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setName(dbUser?.name || '');
  }, [dbUser?.name]);

  const trimmed = name.trim();
  const hasChange = trimmed !== (dbUser?.name || '');
  const canSave = !!token && trimmed.length > 0 && hasChange && !saving;

  const handleSave = async () => {
    if (!token || !trimmed) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateMyName(token, trimmed);
      await refreshUser();
      setSuccess(
        updated.auth0SyncWarning
          ? `Name updated. (${updated.auth0SyncWarning})`
          : 'Name updated.'
      );
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update your name. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!dbUser) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2} sx={{ maxWidth: 480 }}>
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      <TextField
        label="Display Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        size="small"
        fullWidth
        disabled={saving}
        inputProps={{ maxLength: 100 }}
        helperText="Shown in the dashboard greeting and account menu."
      />

      <TextField
        label="Login Email"
        value={dbUser.email}
        size="small"
        fullWidth
        disabled
        helperText="Managed by your administrator."
      />

      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body2" color="text.secondary">
          Role:
        </Typography>
        <Chip label={ROLE_LABELS[role || ''] || role || '—'} size="small" />
      </Box>

      <Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!canSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default MyAccountEditor;
